import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BaseButton from '../../components/Common/BaseButton';
import UnifiedDocumentModal from '../../components/Common/UnifiedDocumentModal';
import RegenerateModal from "../../components/Common/RegenerateModal";
import { regenerateKeywordCluster } from "../../utils/blog";
import { toast } from "react-toastify";
import { getAgentId } from "../../helpers";


const KeywordCluster = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialData = location.state?.clusterData;
  const sessionId = location.state?.session_id;
  const agentId = location.state?.agent_id;
  const UID = location.state?.uid || "";

  const [clusters, setClusters] = useState(() => {
    const clustersData = initialData?.semantic_clusters;
    if (Array.isArray(clustersData)) return clustersData;
    if (clustersData && typeof clustersData === 'object') return [clustersData];
    return [];
  });
  const [showForm, setShowForm] = useState(false);
  const [newClusterName, setNewClusterName] = useState("");
  const [newKeywordsArr, setNewKeywordsArr] = useState([""]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  // State for inline editing
  const [editingClusterIndex, setEditingClusterIndex] = useState(null);
  const [editableClusterName, setEditableClusterName] = useState("");
  const [editableKeywords, setEditableKeywords] = useState([]);

  // State for regeneration modal
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [regenerateContext, setRegenerateContext] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  if (!initialData || !initialData.semantic_clusters) {
    return <div style={{ padding: 32 }}>No keyword cluster data found.</div>;
  }

  const handleAddCluster = () => {
    if (!newClusterName.trim() || newKeywordsArr.every(k => !k.trim())) return;
    // Split by comma and flatten, then trim and filter empty values
    const keywordsArray = newKeywordsArr
      .flatMap(k => k.split(','))
      .map(k => k.trim())
      .filter(Boolean);
    const newCluster = { id: `temp-${Date.now()}`, cluster_name: newClusterName, keywords: keywordsArray };
    setClusters([newCluster, ...clusters]);
    setNewClusterName("");
    setNewKeywordsArr([""]);
    setShowForm(false);
  };

  const handleEditCluster = (index) => {
    setEditingClusterIndex(index);
    setEditableClusterName(clusters[index].cluster_name);
    setEditableKeywords([...clusters[index].keywords]);
  };

  const handleSaveEdits = (index) => {
    // Split by comma and flatten, then trim and filter empty values
    const processedKeywords = editableKeywords
      .flatMap(k => k.split(','))
      .map(k => k.trim())
      .filter(Boolean);
    const updatedClusters = [...clusters];
    updatedClusters[index] = {
      ...updatedClusters[index],
      cluster_name: editableClusterName,
      keywords: processedKeywords,
    };
    setClusters(updatedClusters);
    setEditingClusterIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingClusterIndex(null);
  };

  const handleDeleteCluster = (index) => {
    const updatedClusters = clusters.filter((_, i) => i !== index);
    setClusters(updatedClusters);
  };

  const handleClusterNameChange = (e) => {
    setEditableClusterName(e.target.value);
  };

  const handleKeywordChange = (keywordIndex, e) => {
    const updatedKeywords = [...editableKeywords];
    updatedKeywords[keywordIndex] = e.target.value;
    setEditableKeywords(updatedKeywords);
  };

  const handleAddKeywordInput = () => {
    setEditableKeywords([...editableKeywords, ""]);
  };

  const handleRemoveKeywordInput = (keywordIndex) => {
    const updatedKeywords = editableKeywords.filter((_, i) => i !== keywordIndex);
    setEditableKeywords(updatedKeywords);
  };

  const handleOpenRegenerateModal = (index) => {
    setRegenerateContext({ index });
    setIsRegenerateModalOpen(true);
  };

  const handleRegenerateSubmit = async (customPrompt) => {
    if (!regenerateContext) return;
    setIsRegenerating(true);
    const { index } = regenerateContext;
    const clusterToRegenerate = clusters[index];

    try {
      // Get the KeywordClusterSmith agent ID from localStorage (from login)
      const keywordClusterAgentId = getAgentId("KeywordClusterSmith");
      if (!keywordClusterAgentId) {
        toast.error("KeywordClusterSmith agent ID not found. Please log in again.");
        setIsRegenerateModalOpen(false);
        setRegenerateContext(null);
        setIsRegenerating(false);
        return;
      }

      const result = await regenerateKeywordCluster({
        agentId: keywordClusterAgentId,
        sessionId,
        uid: UID,
        blogType: initialData.blog_type,
        seedKeywords: clusterToRegenerate.keywords,
        furtherDetails: customPrompt,
        sectionName: clusterToRegenerate.cluster_name
      });
      
      const newCluster = result.semantic_clusters[0];
      const updatedClusters = [...clusters];
      updatedClusters[index] = newCluster;

      setClusters(updatedClusters);
      toast.success("Cluster regenerated successfully!");
    } catch (error) {
      toast.error("Failed to regenerate cluster.");
    } finally {
      setIsRegenerateModalOpen(false);
      setRegenerateContext(null);
      setIsRegenerating(false);
    }
  };

  return (
    <div >
      <div>
        <div class="breadcrumb-container mx-3">
          <div class="breadcrumb-back">
            <span class="back-text" onClick={() => navigate(-1)}>&lt; Back</span>
            </div><nav aria-label="breadcrumb">
              <ol class="breadcrumb">
                <li class="breadcrumb-item">
                  <a href="/">Home</a></li>
                  <span class="breadcrumb-separator"> &gt; </span>
                  <li class="breadcrumb-item ">
                    <a href="/skills">Skills</a></li>
                    <span class="breadcrumb-separator"> &gt; </span>
                    <li class="breadcrumb-item ">
                    <a href="/skills/blog">Blog templates</a></li>
                    <span class="breadcrumb-separator"> &gt; </span>
                    <li class="breadcrumb-item active">
                      <span>Keyword cluster</span></li></ol>
                      </nav></div>
                      <div class="d-flex justify-content-between align-items-center mb-3 border-bottom pb-3">
                        <div class="title-label">Keyword cluster</div>
                        <div class="d-flex gap-2">
                          
                        <button class="btn bgprimary" onClick={() => setShowForm(true)}> Add New keyword cluster</button>
                        </div>
                      </div>
                     

        {showForm && (
          <div style={{ border: '1px solid #ccc', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            <input
              type="text"
              placeholder="Cluster Name"
              value={newClusterName}
              onChange={(e) => setNewClusterName(e.target.value)}
              className="form-input form-control mb-3"
            />
            {newKeywordsArr.map((kw, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <input
                  type="text"
                  value={kw}
                  onChange={e => {
                    const updated = [...newKeywordsArr];
                    updated[idx] = e.target.value;
                    setNewKeywordsArr(updated);
                  }}
                  onPaste={e => {
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData("text");
                    // Split by comma, newline, or tab
                    const keywords = pastedText
                      .split(/,|\n|\t/)
                      .map((k) => k.trim())
                      .filter((k) => k.length > 0);
                    
                    if (keywords.length > 0) {
                      const updated = [...newKeywordsArr];
                      // Replace current input with first keyword and add rest
                      updated.splice(idx, 1, ...keywords, "");
                      setNewKeywordsArr(updated);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const value = e.target.value.trim();
                      if (value) {
                        if (value.includes(',')) {
                          // Split by comma and add all keywords
                          const splitKeywords = value.split(',').map(k => k.trim()).filter(Boolean);
                          if (splitKeywords.length > 0) {
                            const updated = [...newKeywordsArr];
                            updated.splice(idx, 1, ...splitKeywords, "");
                            setNewKeywordsArr(updated);
                          }
                        } else {
                          // Add single keyword and move to next input
                          const updated = [...newKeywordsArr];
                          updated[idx] = value;
                          if (idx === updated.length - 1) {
                            updated.push("");
                          }
                          setNewKeywordsArr(updated);
                        }
                      }
                    }
                  }}
                  onBlur={e => {
                    const value = e.target.value;
                    if (value.includes(',')) {
                      const splitKeywords = value.split(',').map(k => k.trim()).filter(Boolean);
                      if (splitKeywords.length > 1) {
                        const updated = [...newKeywordsArr];
                        updated.splice(idx, 1, ...splitKeywords);
                        setNewKeywordsArr(updated);
                      }
                    }
                  }}
                  placeholder="Enter keywords "
                  className="form-input form-control mb-3"
                />
                <button
                  onClick={() => setNewKeywordsArr(newKeywordsArr.filter((_, i) => i !== idx))}
                  className="btn btn-outline-secondary mb-3"
                  disabled={newKeywordsArr.length === 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={() => setNewKeywordsArr([...newKeywordsArr, ""])}
              className="btn btn-outline-secondary mb-3"
            >
              + Add Keyword
            </button>
            <div className="d-flex gap-2">
              <button
                className="btn bgprimary"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleAddCluster}
              >
                Save Cluster
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div>
          {clusters.map((cluster, idx) => (
            <div key={cluster.id || idx} style={{ marginBottom: 32, borderBottom: '1px solid #f0f0f0', paddingBottom: '24px' }}>
              {editingClusterIndex === idx ? (
                // Edit Mode
                <div>
                  <input
                    type="text"
                    value={editableClusterName}
                    onChange={handleClusterNameChange}
                    className="form-input form-control mb-3"
                  />
                  {editableKeywords.map((kw, keywordIdx) => (
                      <div key={keywordIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <input
                          type="text"
                          value={kw}
                          onChange={(e) => handleKeywordChange(keywordIdx, e)}
                          onPaste={e => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData("text");
                            // Split by comma, newline, or tab
                            const keywords = pastedText
                              .split(/,|\n|\t/)
                              .map((k) => k.trim())
                              .filter((k) => k.length > 0);
                            
                            if (keywords.length > 0) {
                              const updated = [...editableKeywords];
                              // Replace current input with first keyword and add rest
                              updated.splice(keywordIdx, 1, ...keywords, "");
                              setEditableKeywords(updated);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const value = e.target.value.trim();
                              if (value) {
                                if (value.includes(',')) {
                                  // Split by comma and add all keywords
                                  const splitKeywords = value.split(',').map(k => k.trim()).filter(Boolean);
                                  if (splitKeywords.length > 0) {
                                    const updated = [...editableKeywords];
                                    updated.splice(keywordIdx, 1, ...splitKeywords, "");
                                    setEditableKeywords(updated);
                                  }
                                } else {
                                  // Add single keyword and move to next input
                                  const updated = [...editableKeywords];
                                  updated[keywordIdx] = value;
                                  if (keywordIdx === updated.length - 1) {
                                    updated.push("");
                                  }
                                  setEditableKeywords(updated);
                                }
                              }
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value.includes(',')) {
                              const splitKeywords = value.split(',').map(k => k.trim()).filter(Boolean);
                              if (splitKeywords.length > 1) {
                                const updated = [...editableKeywords];
                                updated.splice(keywordIdx, 1, ...splitKeywords);
                                setEditableKeywords(updated);
                              }
                            }
                          }}
                          placeholder="Enter keywords"
                          className="form-input form-control mb-3"
                        />
                        <button
                          onClick={() => handleRemoveKeywordInput(keywordIdx)}
                          className="btn btn-outline-secondary mb-3"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={handleAddKeywordInput}
                      className="btn btn-outline-secondary mb-3"
                    >
                      + Add Keyword
                    </button>
                  <div className="d-flex gap-2">
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSaveEdits(idx)}
                      className="btn bgprimary"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="btn btn-outline-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="cluster-title">
                      <span style={{ cursor: 'grab', color: '#888' }}>⋮⋮</span>
                      {cluster.cluster_name}
                    </div>
                    <div className="cluster-desc">
                      {cluster.keywords && cluster.keywords.map((kw, i) => (
                        <div key={i}>{kw}</div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                    <button  className="btn btn-outline-secondary" onClick={() => handleOpenRegenerateModal(idx)}>
                      Regenerate text
                    </button>
                    <button
                      onClick={() => handleEditCluster(idx)}
                      className="btn btn-outline-secondary"
                    
                    >
                      Edit text
                    </button>
                    <button
                      onClick={() => handleDeleteCluster(idx)}
                      className="btn btn-outline-danger"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

      
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginTop: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              className="btn bgprimary "
              onClick={() => navigate("/skills/blog/generate-outline", { state: { agent_id: agentId, session_id: sessionId, uid: UID, clusterData: { semantic_clusters: clusters, blog_type: initialData?.blog_type || "" } } })}
            >
              Generate outline
            </button>
            <button
              className="documents-button"
              style={{ color: '#000', fontSize: 15, background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', padding: 0, textAlign: 'left' }}
              onClick={() => setShowDocumentModal(true)}
            >
              Add additional documents
            </button>
          </div>
        </div>
      </div>
      <UnifiedDocumentModal
        show={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        initialTags={""}
        initialDocList={""}
      />
      <RegenerateModal
        isOpen={isRegenerateModalOpen}
        onClose={() => setIsRegenerateModalOpen(false)}
        onSubmit={handleRegenerateSubmit}
      />
      {isRegenerating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.8)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div className="loader-spinner" style={{ fontSize: 48 }}>
            <span className="spinner-border" role="status" aria-hidden="true"></span>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeywordCluster;
